import { Component } from 'preact';
import { motion } from 'framer-motion';
import { GOLD, RADIUS } from '../../lib/medievalTheme';
import { getLocalApiOrigin, resolveLocalImageUrl } from '../../utils/localApi';
import { playUI, UI } from '../../utils/arena/uiSounds';
import { setHoveredCard as setZoomBusHoveredCard } from '../../utils/game/cardZoomBus';
import { pushDismiss, removeDismiss } from '../../utils/escStack';
import { isFoilFinish } from '../../utils/sorcery/foil.js';
import { foilZonesForCard } from '../../utils/sorcery/foilZones.js';
import { DEFAULT_FOIL_PARAMS } from '../../utils/sorcery/foilParams.js';
import CardHoverPreview from '../CardHoverPreview';
import FoilCanvas from '../FoilCanvas';

// Owner-locked unit hands ("Only It Can Cast"), surfaced as RPG-style
// badges. Each badge is the owner unit's card art. Interaction:
//   • HOVER → the first few held cards organically peek out from behind
//     the badge: the leftmost to the left, the middle on top, the rightmost
//     to the right. A teaser that the hand is interactive.
//   • CLICK → those previews fly up and grow into the full vertical fan,
//     and the in-play owner card glows amber. Closing reverses it: the
//     cards shrink back into the badge.
// One card layer per badge is always mounted; each card animates between
// three targets (hidden-in-badge → organic peek → fan) driven by state, so
// framer morphs them smoothly in both directions.
//
// The controller sees faces and can drag a spell out to cast it (dropping
// back over the badge cancels); the opponent sees face-down backs only.
// Sizes in cqw (scale with the canvas).

const BADGE_W = 4.2;
const BADGE_H = 5.9;
const BADGE_GAP = 0.7;
const FAN_W = 5;
const FAN_H = 7;
const FAN_GAP = 0.6;
// Canvas is 16:9 → its height is a fixed 56.25cqw regardless of scale.
const AVAIL_UP = 46;
// Above the player hand (z 1000) so the wide hand div never steals the
// badges' hover / click / drag-back; below the combat lift (1210).
const Z = 1005;
const PEEK_N = 3;
const PEEK_SCALE = 0.85; // peek cards roughly as large as the badge itself
const PEEK_X = BADGE_W * 0.52; // side-peek horizontal offset
// Small deterministic per-card wobble so the peek looks hand-placed, not
// mirror-symmetric (indexed by slot; up to PEEK_N cards).
const PEEK_JITTER = [
  { x: -0.2, y: 0.18, rot: -4 },
  { x: 0.12, y: -0.16, rot: 3 },
  { x: 0.28, y: 0.06, rot: 6 },
];
// Amber. Matches the in-play owner glow (handState.setUnitHandGlow, 0xffbf40).
const AMBER = '#ffbf40';

// Per-card animation target. Three states:
//   open    → vertical fan above (below for the top row), full size.
//   peeking → organic peek: left card left, middle up, right card right.
//   neither → collapsed into the badge centre (invisible).
function cardTarget(i, n, peekCount, { open, peeking, fanUp }) {
  const dir = fanUp ? -1 : 1;
  if (open) {
    const step = Math.min(FAN_H + FAN_GAP, AVAIL_UP / Math.max(n, 1));
    const dist = BADGE_H / 2 + FAN_H / 2 + FAN_GAP + i * step;
    return { x: 0, y: dir * dist, rot: 0, scale: 1, opacity: 1 };
  }
  if (peeking && i < peekCount) {
    const t = peekCount === 1 ? 0 : (i / (peekCount - 1)) * 2 - 1; // -1 (left) .. 1 (right)
    const mid = BADGE_H * 0.42;
    const side = BADGE_H * 0.12;
    const j = PEEK_JITTER[i] || { x: 0, y: 0, rot: 0 };
    return {
      x: t * PEEK_X + j.x,
      y: dir * (side + (mid - side) * (1 - Math.abs(t))) + dir * j.y,
      rot: t * 12 + j.rot,
      scale: PEEK_SCALE,
      opacity: 1,
    };
  }
  return { x: 0, y: 0, rot: 0, scale: 0, opacity: 0 };
}

// The real card-back art (matches the hand / opponent meshes). Unit-hand
// spells use the spellbook back; sites would use the atlas back.
function cardBackSrc(isSite = false) {
  return `${getLocalApiOrigin()}/game-assets/${isSite ? 'cardback-atlas-rounded.png' : 'cardback-spellbook-rounded.png'}`;
}

function BackImage({ isSite }) {
  return (
    <img src={cardBackSrc(isSite)} alt="" draggable={false}
      style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
  );
}

// Card art that degrades to the matching card back if `src` is missing or
// the image fails to load (a slug/cache miss must not leave a blank rect).
class CardArt extends Component {
  state = { failed: false };
  render({ src, isSite }, { failed }) {
    if (!src || failed) return <BackImage isSite={isSite} />;
    return (
      <img src={src} alt="" draggable={false}
        onError={() => this.setState({ failed: true })}
        style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
    );
  }
}

// Canvas-local rect of a fanned card, computed from the badge's layout
// rect + the card's slot geometry (the SAME math cardTarget uses for the
// fan). Deterministic and transform-independent. Reading
// getBoundingClientRect off the framer-transformed card element is
// unreliable mid-animation, which mis-placed the hover preview.
function fanCardRect(ownerId, slot, fanUp, total) {
  const badge = document.querySelector(`[data-unit-badge="${ownerId}"]`);
  const canvas = document.querySelector('.app-canvas');
  if (!badge || !canvas) return null;
  const br = badge.getBoundingClientRect();
  const cr = canvas.getBoundingClientRect();
  const cqwPx = cr.width / 100;
  const step = Math.min(FAN_H + FAN_GAP, AVAIL_UP / Math.max(total, 1));
  const distPx = (BADGE_H / 2 + FAN_H / 2 + FAN_GAP + slot * step) * cqwPx;
  const cx = br.left + br.width / 2;
  const cy = br.top + br.height / 2 + (fanUp ? -1 : 1) * distPx;
  const halfW = (FAN_W / 2) * cqwPx;
  const halfH = (FAN_H / 2) * cqwPx;
  return {
    left: (cx - halfW) - cr.left,
    right: (cx + halfW) - cr.left,
    top: (cy - halfH) - cr.top,
    bottom: (cy + halfH) - cr.top,
    canvasWidth: cr.width,
    canvasHeight: cr.height,
  };
}

// Always-mounted card layer for one badge. Each card morphs between the
// three targets above as `open` / `peeking` change. Behind the badge art
// while peeking (zIndex 0), above it when the fan is open (zIndex 2).
class UnitHandCards extends Component {
  render({ entry, ownerId, canPlay, host, open, peeking, fanUp, dropIndex, ghostSpell, onHoverSpell, onLeaveSpell, onSpellDragStart }) {
    const reals = entry.spells || [];
    // While dragging a card over the hand, render it as a "ghost" in the
    // fan at dropIndex (its table mesh is hidden) so it reads as a hand
    // card, reordering live. Exactly the main-hand hand↔table transition.
    let list = reals;
    let ghostIdx = -1;
    if (open && ghostSpell && dropIndex != null) {
      ghostIdx = Math.max(0, Math.min(dropIndex, reals.length));
      list = [...reals];
      list.splice(ghostIdx, 0, ghostSpell);
    }
    const n = list.length;
    const peekCount = Math.min(n, PEEK_N);
    // Server-parity entitlement: a face is shown only to the hand's controller,
    // a seat that has SEEN the spell (sticky reveal), or sandbox (both seats
    // local). The server projection already strips cardId from everyone else, so
    // this is the render's own guard. A stray/injected cardId (e.g. a foreign
    // session blob) can never surface a face to a seat that isn't entitled.
    const viewerSeat = host.props.isCasualSandbox ? null : host._localSeat();
    const entitled = (spell) => host.props.isCasualSandbox
      || entry.controller === viewerSeat
      || (spell._seenBy && spell._seenBy.includes(viewerSeat));
    return (
      <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: open ? 2 : 0 }}>
        {list.map((spell, i) => {
          const isGhost = i === ghostIdx;
          const t = cardTarget(i, n, peekCount, { open, peeking, fanUp });
          // Visibility is per-spell: a face is shown when the spell carries
          // face data, which the sync only delivers to seats entitled to see
          // it (controller, or a seat that has seen it. Sticky reveal). A
          // seat that has merely seen the cards can VIEW them but not play
          // them; dragging-to-cast is gated on `canPlay` (local seat controls).
          // Face art: the spell's own imageUrl, else the catalog art keyed by
          // cardId. Server-drawn owner-locked spells carry cardId but no
          // imageUrl, so without the catalog fallback the controller. Who IS
          // entitled to the face. Saw a back on the fanned card (the hover
          // preview already resolved via cardId, which is why it looked right).
          // A back-only spell (opponent's view) has neither → url stays null.
          const canSeeFace = entitled(spell);
          const catalogArt = (canSeeFace && spell.cardId) ? host._getFullCard(spell.cardId)?.printings?.[0]?.image_url : null;
          const url = resolveLocalImageUrl(canSeeFace ? (spell.imageUrl || catalogArt) : null);
          const interactive = open && canPlay && !isGhost;
          // Premium WebGL foil on face-up foil spells: a per-card FoilCanvas
          // mounted inside the fanned card rides its CSS transform. Skip the
          // drag ghost (its real mesh shows the foil on the table) and anything
          // shown as a back (no url).
          const showFoil = !isGhost && url && isFoilFinish(spell.foiling);
          return (
            <UnitHandSpellCard
              key={spell.id}
              spell={spell}
              url={url}
              showFoil={showFoil}
              t={t}
              peeking={peeking}
              interactive={interactive}
              zIndex={i}
              onMouseEnter={interactive ? () => onHoverSpell(spell, { ownerId, slot: i, fanUp, total: n }) : undefined}
              onMouseLeave={interactive ? () => onLeaveSpell() : undefined}
              onMouseDown={interactive ? (e) => { onSpellDragStart(spell.id, i); host.beginUnitHandDrag(e, spell, ownerId); } : undefined}
            />
          );
        })}
      </div>
    );
  }
}

// One fanned unit-hand spell. Face-up foil spells mount a per-card FoilCanvas
// as a child of the fanned card div, so the WebGL foil rides the card's exact
// CSS transform (fan rotation, scale, rounded corners) for free. The live
// contexts stay bounded by FoilCanvas's global FOIL_CANVAS_MAX cap; the DOM
// <img> underneath shows when a card is over budget.
function UnitHandSpellCard({ spell, url, showFoil, t, peeking, interactive, zIndex, onMouseEnter, onMouseLeave, onMouseDown }) {
  return (
    <motion.div
      data-unit-spell={spell.id}
      initial={false}
      animate={{ x: `${t.x}cqw`, y: `${t.y}cqw`, rotate: t.rot, scale: t.scale, opacity: t.opacity }}
      transition={peeking
        ? { type: 'spring', stiffness: 700, damping: 34, restDelta: 0.001 } // snap out instantly on hover
        : { type: 'spring', stiffness: 320, damping: 26 }}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onMouseDown={onMouseDown}
      style={{
        position: 'absolute',
        left: `${(BADGE_W - FAN_W) / 2}cqw`,
        bottom: `${(BADGE_H - FAN_H) / 2}cqw`,
        width: `${FAN_W}cqw`,
        height: `${FAN_H}cqw`,
        borderRadius: RADIUS.card,
        overflow: 'hidden',
        backgroundColor: '#0e0c08',
        boxShadow: '0 0.5cqw 1.4cqw rgba(0,0,0,0.8), 0 0 0 1px rgba(232,195,110,0.5)',
        transformOrigin: 'center',
        pointerEvents: interactive ? 'auto' : 'none',
        zIndex,
      }}
    >
      <CardArt src={url} isSite={spell.isSite} />
      {showFoil ? (
        <FoilCanvas
          imageUrl={url}
          zones={foilZonesForCard(spell)}
          params={DEFAULT_FOIL_PARAMS}
          rotate90={!!spell.isSite}
        />
      ) : null}
    </motion.div>
  );
}

export default class UnitHandBadges extends Component {
  state = { hoveredOwnerId: null, openOwnerId: null, preview: null, draggingSpellId: null, draggingOwnerId: null, dropIndex: null };

  _escToken = null;
  _draggingFromHand = false;
  _docListening = false;
  // False until the pointer genuinely moves after the fan opens. Opening
  // animates cards up under a stationary cursor, firing a synthetic
  // mouseenter on the top card. A preview the player never asked for. We
  // ignore card hover until a real move arms it.
  _hoverArmed = false;

  armHover = () => { this._hoverArmed = true; };

  componentWillUnmount() {
    setZoomBusHoveredCard(null);
    this.props.host._hoveredUnitSpell = null;
    this.props.host._setUnitHandGlow?.(null);
    this.detachOpenWatchers();
  }

  // The wrapper DOM node of the currently-open hand. Used to decide whether
  // a click / drag-release landed "inside" the open hand.
  openWrapper() {
    const id = this.state.openOwnerId;
    return id ? document.querySelector(`[data-unit-badge="${id}"]`) : null;
  }

  attachOpenWatchers() {
    if (this._docListening) return;
    this._docListening = true;
    document.addEventListener('mousedown', this.onDocMouseDown, true);
    document.addEventListener('mouseup', this.onDocMouseUp, true);
    document.addEventListener('mousemove', this.armHover, true);
    this._escToken = pushDismiss(this.closeHand);
  }

  detachOpenWatchers() {
    if (this._docListening) {
      document.removeEventListener('mousedown', this.onDocMouseDown, true);
      document.removeEventListener('mouseup', this.onDocMouseUp, true);
      document.removeEventListener('mousemove', this.armHover, true);
      this._docListening = false;
    }
    document.removeEventListener('mousemove', this.onUnitDragMove);
    removeDismiss(this._escToken);
    this._escToken = null;
    this._draggingFromHand = false;
  }

  // Begin tracking a drag out of the open fan. Beyond the drag itself
  // (handled by the host), we follow the cursor over the WHOLE hand zone
  // to preview an insertion slot, so the card can be reordered on release.
  startUnitDragTracking = (spellId, ownerId, fanUp, startIndex) => {
    this._draggingFromHand = true;
    this._dragFanUp = fanUp;
    this.clearHoverSpell();
    // Start with the card "in the hand" at the slot it came from (ghost),
    // its table mesh hidden. The card only becomes a playmat mesh once the
    // cursor leaves the hand zone (onUnitDragMove).
    // Live index in a sync instance var. OnDocMouseUp reads this, not the
    // async state, so the release uses the LAST slot shown (not a stale one).
    this._dropIndex = startIndex ?? null;
    this.setState({ draggingSpellId: spellId, draggingOwnerId: ownerId, dropIndex: startIndex ?? null });
    requestAnimationFrame(() => {
      const m = this.props.host.meshes.get(spellId);
      if (m && this.state.dropIndex != null) m.visible = false;
    });
    document.addEventListener('mousemove', this.onUnitDragMove);
  };

  // Map the cursor to a fan slot over the WHOLE hand zone. Over the zone:
  // dropIndex = the slot, the table mesh is hidden, and the card shows as a
  // ghost in the fan (reordering). Outside: dropIndex = null, the mesh is
  // shown (playmat card). Mirrors the main hand's hand↔table handoff.
  onUnitDragMove = (e) => {
    const ownerId = this.state.draggingOwnerId;
    if (!ownerId) return;
    const badge = document.querySelector(`[data-unit-badge="${ownerId}"]`);
    const canvas = document.querySelector('.app-canvas');
    if (!badge || !canvas) return;
    const br = badge.getBoundingClientRect();
    const cqwPx = canvas.getBoundingClientRect().width / 100;
    const count = this.props.host.state.gameState?.unitHands?.[ownerId]?.spells?.length || 0;
    const fanUp = this._dragFanUp;
    const stepPx = Math.min(FAN_H + FAN_GAP, AVAIL_UP / Math.max(count + 1, 1)) * cqwPx;
    const cx = br.left + br.width / 2;
    const base = fanUp ? (br.top - e.clientY) : (e.clientY - br.bottom);
    const overX = Math.abs(e.clientX - cx) < (FAN_W / 2 + 0.6) * cqwPx;
    const overY = base > -br.height && base < (count + 1.6) * stepPx;
    const next = (overX && overY) ? Math.max(0, Math.min(Math.round(base / stepPx), count)) : null;
    if (next === this._dropIndex) return;
    this._dropIndex = next; // sync. Release reads this
    // Toggle the dragged card between hand-ghost (hidden mesh) and playmat.
    const mesh = this.props.host.meshes.get(this.state.draggingSpellId);
    if (mesh) mesh.visible = (next == null);
    if (next != null) { try { playUI(UI.HOVER, { volume: 0.4 }); } catch {} } // reorder tick = hover sound
    this.setState({ dropIndex: next });
  };

  openHand = (ownerId) => {
    // Open click. Same cue as picking a card in the token sidebar.
    this._hoverArmed = false; // re-arm only after the pointer moves (see _hoverArmed)
    playUI(UI.UNEQUIP);
    this.props.host._setUnitHandGlow?.(ownerId); // amber glow on the in-play owner
    this.setState({ openOwnerId: ownerId, hoveredOwnerId: null });
    this.attachOpenWatchers();
  };

  closeHand = () => {
    // Close cue. Same as the action menu closing. Only when one was open.
    if (this.state.openOwnerId) playUI(UI.CANCEL);
    this.props.host._setUnitHandGlow?.(null);
    this.detachOpenWatchers();
    this.clearHoverSpell();
    this.setState({ openOwnerId: null });
  };

  toggleHand = (ownerId) => {
    if (this.state.openOwnerId === ownerId) this.closeHand();
    else this.openHand(ownerId);
  };

  // A plain click outside the open hand closes it.
  onDocMouseDown = (e) => {
    const wrap = this.openWrapper();
    if (wrap && !wrap.contains(e.target)) this.closeHand();
  };

  // Release while dragging a spell out of the hand:
  //   • over the hand zone (dropIndex set) → drop it back at that slot
  //     (reorder), keeping the hand open;
  //   • elsewhere → let the normal play/cast commit run.
  // Capture phase + returnDragToUnitHand nulls host.dragging, so the
  // later canvas/window mouseup commit no-ops for the reorder case.
  onDocMouseUp = () => {
    if (!this._draggingFromHand) return;
    this._draggingFromHand = false;
    document.removeEventListener('mousemove', this.onUnitDragMove);
    const host = this.props.host;
    const dropIndex = this._dropIndex; // sync live value, not the async state
    this._dropIndex = null;
    const dragId = host._unitHandDrag?.spellId;
    const card = dragId ? host.meshes.get(dragId)?.userData?.cardInstance : null;
    this.setState({ draggingSpellId: null, draggingOwnerId: null, dropIndex: null });
    if (dropIndex != null && card) {
      host.returnDragToUnitHand(card, dropIndex); // reorder into the slot; stay open
      return;
    }
    this.closeHand(); // played/cast somewhere on the board
  };

  // [{ ownerId, entry, art }] for unit hands on the given side. Empty hands
  // are dropped unless one is mid-drag (its badge must persist as a target).
  groupsFor(controller) {
    const host = this.props.host;
    const unitHands = host.state.gameState?.unitHands || {};
    const out = [];
    for (const [ownerId, entry] of Object.entries(unitHands)) {
      if (entry.controller !== controller) continue;
      if (!(entry.spells?.length) && ownerId !== this.state.draggingOwnerId) continue;
      // Owner cardId from the authoritative mirror (a public board card),
      // falling back to the live mesh. The mesh lookup alone was unreliable.
      // server-drawn unit hands carry no `ownerArt`, so when the mesh had no
      // cardInstance the badge fell through to a card back for BOTH seats.
      const owner = host.meshes.get(ownerId)?.userData?.cardInstance;
      const ownerCardId = host._authMirror?.getState?.()?.cards?.[ownerId]?.cardId || owner?.cardId;
      const catalogArt = ownerCardId ? host._getFullCard(ownerCardId)?.printings?.[0]?.image_url : null;
      const art = entry.ownerArt || owner?.imageUrl || catalogArt || null;
      out.push({ ownerId, entry, art });
    }
    return out;
  }

  // Hover a fanned spell: big left preview + focused card (Space → inspect)
  // + alt-zoom bus (Alt → zoom). Parity with hovering any other card.
  onHoverSpell = (spell, meta) => {
    // Ignore the synthetic mouseenter the open animation fires on the top
    // card under a stationary cursor. Only a real pointer move arms hover.
    if (!this._hoverArmed) return;
    const host = this.props.host;
    try { playUI(UI.HOVER, { volume: 0.4 }); } catch {}
    const full = host._getFullCard(spell.cardId);
    host._setFocusedPreviewCard(spell);
    setZoomBusHoveredCard(spell);
    host._hoveredUnitSpell = spell;
    const rect = fanCardRect(meta.ownerId, meta.slot, meta.fanUp, meta.total);
    this.setState({ preview: full ? { card: full, foiling: spell.foiling, rect } : null });
  };

  clearHoverSpell = () => {
    setZoomBusHoveredCard(null);
    this.props.host._hoveredUnitSpell = null;
    this.setState({ preview: null });
  };

  renderRow(controller, viewerSeat, anchor) {
    const groups = this.groupsFor(controller);
    if (!groups.length) return null;
    const host = this.props.host;
    // Only the seat that CONTROLS the hand may play from it. A seat that has
    // merely seen the cards (sticky reveal) can open the badge and view them
    // (face data arrives via sync) but gets no drag/cast. Sandbox controls both.
    const canPlay = host.props.isCasualSandbox || controller === viewerSeat;
    const isBottom = anchor.bottom != null;
    const vEdge = isBottom ? 'bottom' : 'top';
    const vBase = isBottom ? anchor.bottom : anchor.top;
    const PER_COLUMN = 3;
    return (
      <div
        style={{
          position: 'fixed', right: anchor.right, [vEdge]: vBase,
          width: 0, height: 0, zIndex: Z, pointerEvents: 'none',
        }}
      >
        {groups.map(({ ownerId, entry, art }, i) => {
          const open = this.state.openOwnerId === ownerId;
          const peeking = !open && this.state.hoveredOwnerId === ownerId;
          const col = Math.floor(i / PER_COLUMN);
          const slot = i % PER_COLUMN;
          return (
            <div
              key={ownerId}
              data-unit-badge={ownerId}
              style={{
                position: 'absolute',
                right: `${col * (BADGE_W + BADGE_GAP)}cqw`,
                [vEdge]: `${slot * (BADGE_H + BADGE_GAP)}cqw`,
                width: `${BADGE_W}cqw`, height: `${BADGE_H}cqw`, pointerEvents: 'auto',
                // Raise the active badge so its peeking cards show above the
                // neighbouring badges (while still sitting below its own art).
                zIndex: (open || peeking) ? 5 : 1,
              }}
              onMouseEnter={() => { if (!open) { try { playUI(UI.HOVER, { volume: 0.4 }); } catch {} this.setState({ hoveredOwnerId: ownerId }); } }}
              onMouseLeave={() => {
                if (!open) this.clearHoverSpell();
                this.setState((s) => (s.hoveredOwnerId === ownerId ? { hoveredOwnerId: null } : null));
              }}
              onClick={() => this.toggleHand(ownerId)}
            >
              <UnitHandCards
                entry={entry}
                ownerId={ownerId}
                canPlay={canPlay}
                host={host}
                open={open}
                peeking={peeking}
                fanUp={isBottom}
                dropIndex={this.state.draggingOwnerId === ownerId ? this.state.dropIndex : null}
                ghostSpell={this.state.draggingOwnerId === ownerId
                  ? host.meshes.get(this.state.draggingSpellId)?.userData?.cardInstance
                  : null}
                onHoverSpell={this.onHoverSpell}
                onLeaveSpell={this.clearHoverSpell}
                onSpellDragStart={(spellId, idx) => this.startUnitDragTracking(spellId, ownerId, isBottom, idx)}
              />
              <motion.div
                data-unit-badge-art=""
                animate={{ scale: (peeking || open) ? 1.05 : 1 }}
                transition={{ type: 'spring', stiffness: 340, damping: 24 }}
                style={{
                  position: 'absolute', inset: 0, zIndex: 1,
                  borderRadius: RADIUS.card, overflow: 'hidden',
                  border: `1px solid ${open ? AMBER : GOLD}`, backgroundColor: '#0e0c08',
                  // Open → persistent amber glow (matches the in-play owner
                  // card). Peek → a subtle gold hint. Idle → plain drop shadow.
                  boxShadow: open
                    ? `0 0 1.4cqw 0.25cqw ${AMBER}`
                    : (peeking ? `0 0 1cqw ${GOLD}` : '0 0.3cqw 0.8cqw rgba(0,0,0,0.7)'),
                }}
              >
                <CardArt src={art ? resolveLocalImageUrl(art) : null} />
              </motion.div>
            </div>
          );
        })}
      </div>
    );
  }

  render() {
    const host = this.props.host;
    const isCasual = host.props.isCasualSandbox;
    const bottomSeat = isCasual ? 'p1' : host._localSeat();
    const topSeat = bottomSeat === 'p1' ? 'p2' : 'p1';
    const viewerSeat = isCasual
      ? (host.state.activePlayerNum === 2 ? 'p2' : 'p1')
      : host._localSeat();
    return (
      <div aria-hidden="true">
        {this.renderRow(topSeat, viewerSeat, { top: '1cqw', right: '10.5cqw' })}
        {this.renderRow(bottomSeat, viewerSeat, { bottom: '1cqw', right: '10.5cqw' })}
        {this.state.preview ? (
          <CardHoverPreview card={this.state.preview.card} foiling={this.state.preview.foiling} rect={this.state.preview.rect} />
        ) : null}
      </div>
    );
  }
}
